<template>
    <div
        v-if="user !== null"
        class="container-fluid">
        <div v-if="isAnswerMode">
            <user-health-survey-answer
                :user="user"
                :options="options"
                :group-metadata="groupMetadata">
            </user-health-survey-answer>
        </div>
        <div
            v-else-if="inProgress"
            class="p-5 d-flex justify-content-center align-items-center">
            <b-spinner variant="yokumiru-user"/>
        </div>
        <div v-else>
            <div class="userHealthSurveysWrapper">
                <div class="headingWrapper text-center">
                    <div class="headingTitle mb-3">健康についての質問回答</div>
                    <div class="headingContent">
                        <div class="headingDescription">あなたの身体の状態をチェックしましょう。</div>
                        <div
                            v-if="isAnswered"
                            class="isAnswered">
                            今日のチェックは回答済みです
                        </div>
                        <div v-else>
                            <div class="headingTimeWrapper d-flex justify-content-center mb-3">
                                <div>毎日</div>
                                <div>{{ helper.timeForHuman(answerStartTimeJst.format('YYYY-MM-DD HH:mm:ss'), 'Asia/Tokyo') }}</div>
                                <div>〜</div>
                                <div>{{ helper.timeForHuman(answerEndTimeJst.format('YYYY-MM-DD HH:mm:ss'), 'Asia/Tokyo') }}</div>
                                <div>までに回答してください</div>
                            </div>
                            <button
                                class="surveyBtn btn btn-info"
                                :disabled="inProgress || !hasQuestion"
                                @click="handleClick">質問に回答する
                            </button>
                            <div v-if="!hasQuestion" class="text-muted small mt-1">今日は回答不要です（質問がありません）</div>
                        </div>
                    </div>
                </div>
                <div class="healthSurveyLogsWrapper">
                    <div
                        v-for="surveyDate in surveyDates"
                        class="healthSurveyLogItem d-flex justify-content-between align-items-center"
                        :class="{ 'answered' : healthSurveyLogs[surveyDate] && healthSurveyLogs[surveyDate][0].answer_at !== null }">
                        <div>
                            <img :src="iconPath(healthSurveyLogs[surveyDate])">
                            <span class="pl-2">{{ helper.userTime(`${surveyDate} 00:00:00`, 'Asia/Tokyo').format('YYYY年M月D日') }}</span>
                        </div>
                        <div>
                            <span v-if="!healthSurveyLogs[surveyDate]">未回答</span>
                            <span v-else>{{ `${helper.timeForHuman(healthSurveyLogs[surveyDate][0].answer_at, user.timezone)}に回答済み` }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import constants from '../../constants'
import moment from 'moment-timezone'
import YokumiruHelper from '../../YokumiruHelper'

export default {
    props: {
        groupMetadata: {
            type: Object,
            required: true,
        },
    },

    data: function () {
        return {
            inProgress: false,
            helper: YokumiruHelper,
            user: null,
            accessToken: null,
            healthSurveyLogs: null,
            healthSurveyQuestions: [],
            interval: null,
            currentJst: null,
            current: null,
            isAnswerMode: false,
            errors: {},
        }
    },

    computed: {
        hasQuestion: function () {
            return this.healthSurveyQuestions.length > 0
        },

        daysAgo13: function () {
            return this.helper.userTimeNow(this.user.timezone).subtract(13, 'days').startOf('day')
        },

        surveyDates: function () {
            const now = this.helper.userTimeNow(this.user.timezone)
            const enabledDate = this.helper.userTime(this.groupMetadata.health_survey_enabled_at, this.user.timezone).startOf('day')
            const thresholdDate = enabledDate.isAfter(this.daysAgo13) ? enabledDate : this.daysAgo13
            const result = []

            while (now.diff(thresholdDate) >= 0) {
                result.push(now.format('YYYY-MM-DD'))
                now.subtract(1, 'days')
            }
            return result
        },

        latestSurveyLog: function () {
            if (this.healthSurveyLogs === null || this.healthSurveyLogs.length < 1 || !this.current) {
                return null
            }
            const dateFormat = this.current.format('YYYY-MM-DD')
            return this.healthSurveyLogs[dateFormat] ? this.healthSurveyLogs[dateFormat][0] : null
        },

        answerStartTimeJst: function () {
            const now = moment()
            return this.helper.jstTime(`${now.format('YYYY-MM-DD')} ${this.groupMetadata.health_survey_start_time}`, 'Asia/Tokyo')
        },

        answerEndTimeJst: function () {
            const now = moment()
            return this.helper.jstTime(`${now.format('YYYY-MM-DD')} ${this.groupMetadata.health_survey_end_time}`, 'Asia/Tokyo')
        },

        isAnswered: function () {
            if (!this.latestSurveyLog || !this.current || !this.user) {
                return false
            }
            return this.latestSurveyLog.answer_date === this.current.format('YYYY-MM-DD')
        },

        options: function () {
            if (this.accessToken) {
                return { headers: { 'Authorization': `Bearer ${this.accessToken}` } }
            }
            return null
        },
    },

    created: async function () {
        moment.locale('ja')

        this.accessToken = this.$route.query.access_token
        await this.fetchMe()
        await this.fetchHealthSurveyLogs()
        await this.fetchHealthSurveyQuestions()

        this.currentJst = this.helper.jstTimeNow()
        this.current = this.helper.userTimeNow(this.user.timezone)
        this.interval = setInterval(() => {
            this.currentJst = this.helper.jstTimeNow()
            this.current = this.helper.userTimeNow(this.user.timezone)
        }, 1000)

        bus.$on('health-survey-answered', this.fetchHealthSurveyLogs)
        bus.$on('health-survey-back', () => this.isAnswerMode = false)
    },

    beforeDestroy: function () {
        bus.$off('health-survey-answered')
        bus.$off('health-survey-back')
    },

    methods: {
        /**
         * ログインユーザ取得
         */
        fetchMe: function () {
            return axios.get(constants.user.me.endpoint.base, this.options)
                .then(({data}) => {
                    this.user = data.data
                })
        },

        /**
         * ヘルスアンサー回答ログ一覧取得
         */
        fetchHealthSurveyLogs: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.user.health_survey_logs.endpoint.base

            await axios.get(endpoint, this.options)
                .then(({data}) => {
                    this.healthSurveyLogs = data.data
                })

            this.inProgress = false
        },

        // /**
        //  * ヘルスアンサー詳細設定取得
        //  */
        // fetchHealthSurveyGroupDetails: async function () {
        //     if (this.inProgress) return
        //     this.inProgress = true
        //
        //     const endpoint = constants.user.health_survey_group_details.endpoint.base
        //
        //     await axios.get(endpoint, this.options)
        //         .then(({data}) => {
        //             this.healthSurveyGroupDetails = data.data
        //         })
        //
        //     this.inProgress = false
        // },

        /**
         * ヘルスアンサー質問取得
         */
        fetchHealthSurveyQuestions: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.user.groups.endpoint.base
            await axios.get(`${endpoint}/${this.groupMetadata.group_id}/health_survey/questions`, this.options)
                .then(({data}) => {
                    this.healthSurveyQuestions = data.data
                })

            this.inProgress = false
        },

        /**
         * アイコンのパスを返す
         * @param {object|undefined} healthSurveyLog
         * @return {string}
         */
        iconPath: function (healthSurveyLog) {
            return healthSurveyLog
                ? '/images/health_surveys/icon-health-survey-answered.svg'
                : '/images/health_surveys/icon-health-survey.svg'
        },

        /**
         * 質問に回答するボタン押下
         */
        handleClick: function () {
            this.isAnswerMode = true
        },
    },
}
</script>

<style lang="scss" scoped>
.userHealthSurveysWrapper {
    max-width: 768px;
    margin: 0 auto;

    .headingWrapper {
        .headingTitle {
            color: #504F4F;
            font-size: 18px;
            font-weight: bold;
        }
    }

    .headingContent {
        max-width: 400px;
        margin: 0 auto 25px;
        padding: 15px;
        background: #C6F8F4;
        border-radius: 10px;

        .headingDescription {
            white-space: pre-wrap;
            word-wrap: break-word;
        }

        .headingTimeWrapper {
            margin: 10px 0;
            padding: 3px 6px;
            color: #504F4F;
            font-size: 13px;
            background: #F2F2F2;
            border-radius: 3px;

            @media screen and (max-width:390px) {
                padding: 3px;
                font-size: 12px;
            }

            div {
                &:nth-child(2),
                &:nth-child(3),
                &:nth-child(4) {
                    color: #DD2525;
                    font-weight: bold;
                }
            }
        }

        .isAnswered {
            width: 80%;
            padding: 4px;
            margin: 10px auto 0;
            color: #504F4F;
            font-size: 14px;
            font-weight: bold;
            background: #F2F2F2;
            border-radius: 3px;
        }

        .surveyBtn {
            width: 80%;
            margin: 0 auto;
            border-radius: 42px;

            &:disabled {
                opacity: 0.4;
            }
        }
    }

    .healthSurveyLogsWrapper {
        .healthSurveyLogItem {
            margin-bottom: 10px;
            padding: 10px 15px;
            color: #504F4F;
            background: #E4E4E4;
            border: 1px solid #9E9E9E;
            border-radius: 5px;
            box-sizing: border-box;

            &.answered {
                background: #FFFFFF;
                border: 1px solid #56CED6;

                div {
                    &:nth-child(2) {
                        color: #56CED6;
                    }
                }
            }
        }
    }
}
</style>
